// SM-2 spaced repetition algorithm — a small, dependency-free
// implementation tuned for this app's needs.
//
// Rating scale (1-4) is mapped from the four review buttons:
//   1 = Again — failed, reset to learning
//   2 = Hard  — barely remembered, smaller-than-normal step
//   3 = Good  — recalled comfortably, normal step
//   4 = Easy  — recalled instantly, larger step
//
// Card state machine:
//   new      → never reviewed
//   learning → in the short-step phase before graduation (interval < 1d)
//   review   → graduated, interval grows by `ease` on each Good rating
//   suspended→ user-paused (not surfaced; not changed here)
//
// Returns the patch you'd apply to the row plus a derived rating log
// payload — caller writes both in a single transaction so the
// english_review_log entry and the english_vocab update can't drift.

export const RATING = Object.freeze({
  AGAIN: 1,
  HARD: 2,
  GOOD: 3,
  EASY: 4,
})

// Ease bounds — Anki's defaults. We never let the ease drift below
// 1.3 because the practical experience past that is "this card never
// graduates" and the user should just suspend it instead.
const MIN_EASE = 1.3
const DEFAULT_EASE = 2.5

// Learning-phase steps in days. Sub-1d intervals stay as fractional
// days so a single SQL DATE column can still hold the due-date — we
// compute that as "today + ceil(interval)" so anything <1 day falls
// due today.
const LEARNING_STEPS_DAYS = [1 / 1440 * 10, 10 / 60 / 24] // 10 min, 10 min (a soft buffer)
const GRADUATION_DAYS = 1
const EASY_GRADUATION_DAYS = 4

export function applySrsRating(card, rating) {
  const today = isoDate(new Date())
  const cur = {
    state: card?.srs_state || 'new',
    ease: clampEase(card?.srs_ease ?? DEFAULT_EASE),
    interval: Math.max(0, Number(card?.srs_interval_days ?? 0)),
    reps: Math.max(0, Number(card?.srs_reps ?? 0)),
    lapses: Math.max(0, Number(card?.srs_lapses ?? 0)),
  }
  const prevInterval = cur.interval

  let nextState = cur.state
  let nextEase = cur.ease
  let nextInterval = cur.interval
  let nextReps = cur.reps + 1
  let nextLapses = cur.lapses

  if (rating === RATING.AGAIN) {
    // Lapse — drop ease by 0.2, reset to learning, schedule first
    // step. Reps still increments (we count attempts, not successes).
    nextEase = clampEase(cur.ease - 0.2)
    nextState = 'learning'
    nextInterval = LEARNING_STEPS_DAYS[0]
    nextLapses = cur.lapses + 1
  } else if (cur.state === 'new' || cur.state === 'learning') {
    // Learning phase — Hard repeats the current step, Good graduates
    // to 1 day, Easy jumps to 4 days.
    if (rating === RATING.HARD) {
      nextState = 'learning'
      nextInterval = LEARNING_STEPS_DAYS[Math.min(1, LEARNING_STEPS_DAYS.length - 1)]
    } else if (rating === RATING.GOOD) {
      nextState = 'review'
      nextInterval = GRADUATION_DAYS
    } else if (rating === RATING.EASY) {
      nextState = 'review'
      nextInterval = EASY_GRADUATION_DAYS
      nextEase = clampEase(cur.ease + 0.15)
    }
  } else {
    // Review phase — SM-2 interval multiplication.
    if (rating === RATING.HARD) {
      nextEase = clampEase(cur.ease - 0.15)
      nextInterval = Math.max(1, cur.interval * 1.2)
    } else if (rating === RATING.GOOD) {
      nextInterval = Math.max(1, cur.interval * cur.ease)
    } else if (rating === RATING.EASY) {
      nextEase = clampEase(cur.ease + 0.15)
      nextInterval = Math.max(1, cur.interval * cur.ease * 1.3)
    }
  }

  const nextDueDate = computeDueDate(nextInterval)

  return {
    patch: {
      srs_state: nextState,
      srs_ease: round(nextEase, 2),
      srs_interval_days: round(nextInterval, 3),
      srs_due_date: nextDueDate,
      srs_reps: nextReps,
      srs_lapses: nextLapses,
      updated_at: new Date().toISOString(),
    },
    log: {
      rating,
      prev_interval_days: round(prevInterval, 3),
      new_interval_days: round(nextInterval, 3),
      reviewed_at: new Date().toISOString(),
    },
    today,
    due: nextDueDate,
  }
}

// Predicate the queue uses to decide which rows belong in today's
// session. A null due_date counts as "due today" — new cards land
// here right after enrichment.
export function isDueToday(card, todayIso = isoDate(new Date())) {
  if (!card) return false
  if (card.srs_state === 'suspended') return false
  if (!card.srs_due_date) return true
  return card.srs_due_date <= todayIso
}

function clampEase(e) {
  if (!Number.isFinite(e)) return DEFAULT_EASE
  return Math.max(MIN_EASE, e)
}

function round(n, digits) {
  const f = 10 ** digits
  return Math.round(n * f) / f
}

function computeDueDate(intervalDays) {
  const ms = Math.max(0, intervalDays) * 24 * 60 * 60 * 1000
  // Round up so sub-day intervals land today (not yesterday) and
  // whole-day intervals land on the intended day regardless of TZ.
  const dt = new Date(Date.now() + ms)
  return isoDate(dt)
}

function isoDate(d) {
  return d.toISOString().slice(0, 10)
}
